import { useState } from 'react'
import { IconBackArrowLeft, IconChevronRightSmall } from '@holbonkit/icons'
import {
    BottomSheet,
    BottomSheetBody,
    BottomSheetClose,
    BottomSheetContent,
    BottomSheetHeader,
    BottomSheetTitle,
    BottomSheetTrigger,
    Box,
    Button,
    Text,
} from '@holbonkit/ui'

import styles from './documents-bottom-sheet.module.scss'

type TDocumentType = {
    id: number
    name: string
    value: string
}

const TYPES_OF_DOCUMENTS: TDocumentType[] = [
    {
        id: 1,
        name: 'Паспорт иностранного гражданина',
        value: 'PF',
    },
    {
        id: 2,
        name: 'Свид-во о рассмотрении ходатайства о признании лица беженцем на территории РФ',
        value: 'RCS',
    },
    {
        id: 3,
        name: 'Вид на жительство в Российской Федерации лица без гражданства',
        value: 'RID',
    },
    {
        id: 4,
        name: 'Удостоверение беженца',
        value: 'RAC',
    },
    {
        id: 5,
        name: 'Свидетельство о предоставлении временного убежища на территории РФ',
        value: 'PTS',
    },
]

type TDocumentsBottomSheetProps = {
    errorText?: string
    name?: string
    onBlur?: () => void
    onChange: (value: string) => void
    value: string
}

export const DocumentsBottomSheet = ({
    errorText,
    name,
    onBlur,
    onChange,
    value,
}: TDocumentsBottomSheetProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const selectedDocumentType = TYPES_OF_DOCUMENTS.find((type) => type.value === value) ?? null

    return (
        <Box className={styles['document-type-field']}>
            <BottomSheet
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open)
                    if (!open) {
                        onBlur?.()
                    }
                }}
            >
                <BottomSheetTrigger>
                    <Button
                        className={`${styles['trigger-button']} ${errorText && styles['trigger-button--error']}`}
                        data-test-id='documents__document-type'
                        endIcon={<IconChevronRightSmall color='var(--text-and-icon-secondary)' />}
                        name={name}
                        size='lg'
                        type='button'
                        variant='secondary'
                    >
                        <Box>
                            {selectedDocumentType?.name && (
                                <Text
                                    as='label'
                                    className={styles['trigger-button--label']}
                                    variant='body-s-14-regular'
                                >
                                    Тип документа
                                </Text>
                            )}
                            <Text
                                className={`${styles['trigger-button--text']} ${selectedDocumentType && styles['trigger-button--selected']} ${errorText && styles['trigger-button--error']}`}
                                variant='body-m-16-medium'
                            >
                                {selectedDocumentType?.name ?? 'Тип документа'}
                            </Text>
                        </Box>
                    </Button>
                </BottomSheetTrigger>
                <BottomSheetContent showCloseButton={false}>
                    <BottomSheetHeader className={styles['bottom-sheet__header']}>
                        <BottomSheetTitle>
                            Выберите тип
                            <br />
                            документа
                        </BottomSheetTitle>
                        <BottomSheetClose>
                            <button className={styles['bottom-sheet__header-back']} type='button'>
                                <IconBackArrowLeft color='var(--text-and-icon-primary)' size={24} />
                            </button>
                        </BottomSheetClose>
                    </BottomSheetHeader>
                    <BottomSheetBody className={styles['bottom-sheet-content']}>
                        {TYPES_OF_DOCUMENTS.map((type) => (
                            <Box
                                key={type.id}
                                className={`${styles['bottom-sheet-content__button']} ${selectedDocumentType?.value === type.value && styles['bottom-sheet-content__button--selected']}`}
                                data-test-id={`documents__document-type-option-${type.value}`}
                                onClick={() => {
                                    onChange(type.value)
                                    setIsOpen(false)
                                }}
                            >
                                <Text
                                    className={styles['bottom-sheet-content__button--text']}
                                    variant='body-m-16-medium'
                                >
                                    {type.name}
                                </Text>
                                <IconChevronRightSmall color='var(--text-and-icon-secondary)' />
                            </Box>
                        ))}
                    </BottomSheetBody>
                </BottomSheetContent>
            </BottomSheet>
            {errorText ?
                <Text
                    as='p'
                    className={styles['document-type-field__error']}
                    variant='body-s-14-regular'
                >
                    {errorText}
                </Text>
            :   null}
        </Box>
    )
}
